import type { ReactElement } from 'react';
import type { Vendor } from '../../../../types';
import { normalizeInstagram, normalizeFacebook, normalizeWebsite } from '../../../../utils/socialLinks';
import { PinIcon, InstagramIcon, FacebookIcon, GlobeIcon } from '../../../../components/primitives/Icon';
import { captureEvent } from '../../../../observability';
import shared from './shared.module.css';

interface Props {
  vendor: Vendor;
  locationName?: string | undefined;
}

/**
 * Centered identity shown directly under the category-color hero on both
 * desktop and mobile detail panels. Name, location, and inline social
 * icon links. No avatar — the hero above already carries the category
 * signal.
 */
export function VendorDetailIdentity({ vendor, locationName }: Props) {
  const social: Array<{
    key: string;
    platform: 'instagram' | 'facebook' | 'website';
    href: string;
    label: string;
    icon: ReactElement;
  }> = [];

  const instagramHref = normalizeInstagram(vendor.instagram);
  if (instagramHref) {
    social.push({ key: 'ig', platform: 'instagram', href: instagramHref, label: 'Instagram', icon: <InstagramIcon size={16} /> });
  }

  const facebookHref = normalizeFacebook(vendor.facebook);
  if (facebookHref) {
    social.push({ key: 'fb', platform: 'facebook', href: facebookHref, label: 'Facebook', icon: <FacebookIcon size={16} /> });
  }

  const websiteHref = normalizeWebsite(vendor.website);
  if (websiteHref) {
    social.push({ key: 'web', platform: 'website', href: websiteHref, label: 'Website', icon: <GlobeIcon size={16} /> });
  }

  return (
    <div className={shared.identityCenter}>
      <h3 className={shared.identityName}>{vendor.name}</h3>
      {locationName && (
        <div className={shared.identityLocation}>
          <PinIcon size={12} color="currentColor" />
          <span>{locationName}</span>
        </div>
      )}
      {social.length > 0 && (
        <div className={shared.socialRow}>
          {social.map(s => (
            <a
              key={s.key}
              className={shared.socialLink}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.label}
              title={s.label}
              onClick={() =>
                captureEvent('vendor_link_clicked', {
                  vendor_uid: vendor.uid,
                  vendor_name: vendor.name,
                  platform: s.platform,
                })
              }
            >
              {s.icon}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
